import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import type { Reservation } from "@viajero/proto/guest/v1";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Carousel } from "@/components/Carousel";
import { CancelPolicySection } from "@/features/reservation/components/CancelPolicySection";
import { formatReservationDateTime } from "@/lib/formatReservationDateTime";
import { colors } from "@/theme/colors";

type Props = {
  reservation: Reservation;
  // 店舗名・日時カード内に表示する補足テキスト (画面ごとに異なるため呼び出し側で指定する)。
  message?: ReactNode;
  // 完了画面向けに店舗名カードの上へチェックアイコンを重ねて表示する。
  showConfirmIcon?: boolean;
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        gap: { xs: 0.25, sm: 2 },
        py: 0.75,
      }}
    >
      <Typography sx={{ color: colors.textMuted, fontSize: 14 }}>{label}</Typography>
      <Typography
        sx={{ color: colors.textBody, fontSize: 16, textAlign: { xs: "left", sm: "right" } }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * 予約内容表示 (店舗名・日時カード + 商品画像 + ご購入内容 + キャンセルポリシー)。
 * 予約キャンセル画面 (CancelConfirmStep) とキャンセル完了画面 (CancelCompleteStep) の双方で
 * 同じ内容を表示するため共通化する (JPTP_CANSEI-1269 / -1415)。
 *
 * 表示データはすべて GetReservation (Reservation) 由来。商品画像は予約時点のスナップショット
 * (product_image_url) を 1 枚表示し、キャンセルポリシーは実際の請求に合わせた固定 3 段
 * (§7.4) を出す (JPTP_CANSEI-1477)。
 */
export function ReservationSummaryCard({ reservation, message, showConfirmIcon }: Props) {
  const { t, i18n } = useTranslation();
  const formattedDateTime = formatReservationDateTime(
    reservation.visitDate,
    reservation.visitTime,
    i18n.language,
  );
  const participantSummary = reservation.tickets
    .filter((ticket) => ticket.quantity > 0)
    .map((ticket) => `${ticket.name} x ${ticket.quantity}`)
    .join(" / ");

  return (
    <Box>
      {showConfirmIcon && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
            mb: -3,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: colors.gold,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckIcon sx={{ color: "#fff", fontSize: 24 }} />
          </Box>
        </Box>
      )}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: colors.border,
          px: 3,
          pt: 4,
          pb: 3,
          textAlign: "center",
        }}
      >
        <Typography sx={{ color: colors.textBody, fontWeight: 700, mb: 3 }}>
          {reservation.shopName}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.text, mb: 3 }}>
          {formattedDateTime}
        </Typography>
        {reservation.reservationNumber && (
          <Typography sx={{ color: colors.textMuted, fontSize: 14, mb: 3 }}>
            {t("buyerInfo.reservationNumber")}: {reservation.reservationNumber}
          </Typography>
        )}
        {message && <Typography sx={{ color: colors.textBody }}>{message}</Typography>}
      </Paper>

      <Divider sx={{ mt: 3, mb: 3 }} />

      {reservation.productImageUrl && <Carousel images={[reservation.productImageUrl]} />}

      <Typography variant="h6" sx={{ fontWeight: 700, color: colors.text, mt: 3, mb: 2 }}>
        {t("buyerInfo.purchaseContentTitle")}
      </Typography>
      <SummaryRow label={t("buyerInfo.courseName")} value={reservation.productName} />
      <SummaryRow label={t("buyerInfo.participants")} value={participantSummary} />
      <SummaryRow
        label={t("buyerInfo.totalPrice")}
        value={`¥${reservation.totalAmount.toLocaleString()}`}
      />

      <CancelPolicySection />
    </Box>
  );
}
